import { announceCartFeedback, getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    // Product page: a short "Adding" sweep with an X that stops it before anything is added.
    let countedDown = false;
    if (this.hasAttribute('data-undo-add') && submitter instanceof HTMLElement && !this.hasAttribute('data-stay-on-page')) {
      this.setAttribute('aria-busy', 'true');
      const go = await this.#countdown(submitter, signal);
      if (!go || signal.aborted) {
        this.removeAttribute('aria-busy');
        if (this.#abortController?.signal === signal) this.#abortController = null;
        return;
      }
      countedDown = true;
    }

    this.#setSubmitting(true, submitter, !countedDown);
    const loadingStarted = countedDown ? 0 : performance.now();
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        // Hold the loading state briefly so the spinner reads as intentional, not a flicker.
        const loadingLeft = 600 - (performance.now() - loadingStarted);
        if (loadingLeft > 0) await new Promise((resolve) => { window.setTimeout(resolve, loadingLeft); });
        if (signal.aborted) return;
        this.#setSubmitting(false);
        this.#restoreAfterAdd?.();
        this.#restoreAfterAdd = null;
        // data-stay-on-page (cart page suggestions): no drawer; a short "Added to cart" toast instead, and the new line shows in the cart.
        if (this.hasAttribute('data-stay-on-page')) {
          await announceCartFeedback(this.dataset.addedToast || this.dataset.addedLabel || '', { toast: true });
          return;
        }
        await this.#celebrate(submitter);
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        this.#restoreAfterAdd?.();
        this.#restoreAfterAdd = null;
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  /**
   * Add to cart feedback: the pressed button pops and briefly reads "Added", the product
   * thumbnail (data-fly-image) arcs into the header cart button, which then bounces.
   * Skipped entirely for prefers-reduced-motion. Resolves once the flight lands so the
   * cart drawer opens after it.
   */
  async #celebrate(submitter) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const button = submitter instanceof HTMLElement ? submitter : this.querySelector('button[name="add"]');
    const cart = document.getElementById('header-cart-trigger');

    if (button) {
      button.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(0.96)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }],
        { duration: 450, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
      const label = this.dataset.addedLabel;
      // Label morph: the old text blurs out and the new one blurs into focus.
      const morphLabel = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        [...button.children].forEach((child) => child.animate(
          [{ opacity: 0, filter: 'blur(6px)', transform: 'scale(0.94)' }, { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }],
          { duration: 380, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
        ));
      };
      if (label && !button.dataset.addedOriginal) {
        button.dataset.addedOriginal = button.innerHTML;
        const text = document.createElement('span');
        text.style.cssText = 'display:inline-flex;align-items:center;gap:0.5rem';
        text.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path class="restock-done__check" d="M5 12.5l4.5 4.5L19 7.5" pathLength="1"/></svg>';
        text.append(document.createTextNode(label));
        button.replaceChildren(text);
        morphLabel();
        window.setTimeout(() => {
          if (button.isConnected && button.dataset.addedOriginal) { button.innerHTML = button.dataset.addedOriginal; morphLabel(); }
          delete button.dataset.addedOriginal;
        }, 1600);
      }
    }

    // No flying photo (merchant preference): the bag just gives a small bounce, then the cart opens.
    if (!cart || !cart.getClientRects().length) return;
    document.getElementById('header-cart-trigger')?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(0.94)' }, { transform: 'scale(1)' }],
      { duration: 420, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    );
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  /**
   * "Adding" moment: the label blur-morphs to "Adding" while a soft blue cloud sweeps across the
   * button, then the add goes through (no stop button; merchant preference). Resolves true.
   */
  #countdown(button, signal) {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduce ? 0 : 700;
    const original = button.innerHTML;
    const label = document.createElement('span');
    label.className = 'atc-adding__label';
    label.textContent = this.dataset.addingLabel || 'Adding';
    const sweep = document.createElement('span');
    sweep.className = 'atc-adding__sweep';
    sweep.setAttribute('aria-hidden', 'true');
    button.classList.add('is-adding');
    button.replaceChildren(sweep, label);
    if (!reduce) {
      label.animate(
        [{ opacity: 0, filter: 'blur(6px)', transform: 'scale(0.94)' }, { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }],
        { duration: 320, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
      );
      sweep.animate([{ transform: 'translateX(-100%)' }, { transform: 'translateX(0%)' }], { duration: duration + 300, easing: 'cubic-bezier(0.4, 0, 0.6, 1)', fill: 'forwards' });
    }
    this.#restoreAfterAdd = () => {
      button.classList.remove('is-adding');
      if (button.querySelector('.atc-adding__label')) button.innerHTML = original;
    };
    return new Promise((resolve) => {
      if (signal.aborted) { this.#restoreAfterAdd?.(); resolve(false); return; }
      window.setTimeout(() => resolve(true), duration);
    });
  }

  #restoreAfterAdd = null;

  #setSubmitting(submitting, submitter = this.#activeSubmitter, showSpinner = true) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('cursor-wait');
        if (showSpinner) submitter.setAttribute('data-loading', '');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
      this.#activeSubmitter.removeAttribute('data-loading');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
